//@flow
import * as Immutable from 'immutable';

import { BioData, EmailAddress } from 'common/models/flow/BioData';
import type {
    BioDataModelType,
    EmailAddressModelType
} from 'common/models/flow/BioData';

import {
    validatable,
    validate,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import I18nHelper from 'common/helpers/I18nHelper';
import TimeUtils from 'common/utils/TimeUtils';
import DataUtils from 'common/utils/DataUtils';
import StringUtils from 'common/utils/StringUtils';
import moment from 'moment-timezone';

const translationsNamespace = ['validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

@validatable
class InternalBioData extends BioData {
    static fromJS(json: BioDataModelType): InternalBioData {
        const state: Object = Object.assign({}, json);
        state.emailAddress = state.emailAddress
            ? InternalEmailAddress.fromJS(state.emailAddress)
            : state.emailAddress;
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return !!this.emailAddress?.hasValidationErrors();
    }

    @validateAll
    setAllValidationErrors(): this {
        const updatedEmail = this.emailAddress?.setAllValidationErrors();
        return this.setEmailAddress(updatedEmail);
    }

    @validate
    setDateOfBirth(dateOfBirth: ?string): this {
        return super.setDateOfBirth(dateOfBirth);
    }

    getDateOfBirthMoment() {
        return !DataUtils.isEmpty(this.dateOfBirth)
            ? moment(this.dateOfBirth)
            : null;
    }

    validateDateOfBirth() {
        if (
            !DataUtils.isEmpty(this.dateOfBirth) &&
            !TimeUtils.format(this.dateOfBirth)
        ) {
            return nsTranslate('bioData.invalidDateOfBirth');
        }
        if (TimeUtils.isAfter(this.dateOfBirth, moment())) {
            return nsTranslate('bioData.futureDateOfBirth');
        }
    }

    get emailAddress(): ?InternalEmailAddress {
        return this._state.get('emailAddress');
    }

    setShouldValidateEmailAddress(flag: boolean): this {
        const updatedEmail = this.emailAddress?.setShouldValidateEmailAddress(
            flag
        );
        return this.setEmailAddress(updatedEmail);
    }
}

@validatable
class InternalEmailAddress extends EmailAddress {
    static fromJS(json: EmailAddressModelType): InternalEmailAddress {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        return this;
    }

    @validate
    setEmailAddress(emailAddress: ?string): this {
        return super.setEmailAddress(emailAddress);
    }

    validateEmailAddress(): ?string {
        if (
            this.getShouldValidateEmailAddress() &&
            DataUtils.isEmpty(this.emailAddress)
        ) {
            return nsTranslate(
                'entityManagement:riderValidationErrors.emailEmpty'
            );
        }
        if (this.emailAddress && !StringUtils.isEmail(this.emailAddress)) {
            return nsTranslate('settings:errorMessage.invalidEmailFormat');
        }
    }

    setShouldValidateEmailAddress(flag: boolean): this {
        return this.clone(this._state.set('shouldValidateEmailAddress', flag));
    }

    getShouldValidateEmailAddress(): boolean {
        return this._state.get('shouldValidateEmailAddress');
    }
}

export { InternalBioData as BioData, InternalEmailAddress as EmailAddress };
